import { intoStreamKey } from "../../streams/StreamUtils.js";

export default class StreamEvents {
  #streamMgr;

  constructor(streamMgr) {
    this.#streamMgr = streamMgr;
  }


  /**
   * Pushes a new request range to the data request pipeline for stream events.
   * @param {Array} streamKeys array of streamKeys to request raw data for
   * @param {Array} dateRange [start, end] in milliseconds
   * @param {string} contextId unique ID from the caller, passed back through the DT_STREAM_EVENTS_DATA_EVENT
   */
  async requestStreamEvents(streamKeys, dateRange, contextId) {
    if (!streamKeys?.length) return;
    const model = this.#streamMgr.defaultModel;
    const thresholds = toStreamThresholds(await this.#streamMgr.getAllStreamInfos(), streamKeys);
    return model.asyncPropertyOperation({
      operation: "REQUEST_STREAM_EVENTS",
      streamKeys,
      dateRange,
      thresholds,
      contextId
    });
  }

  /**
   * Get all events in cache within the dateRange.
   * Note: Caller can first call isAllDataAvailable to determine if there are gaps in the data.
   * @param {string[]} streamKeys array of streamKeys
   * @param {Array} dateRange [start, end] in milliseconds
   * @param {boolean} includeInitialEvent defaults false, set true to prepend initial state events at `start` for each stream
   * @param {boolean} grouped true to return an object of event arrays, keyed by streamKey
   * @returns {Array|Object} array of stream event objects, or a grouped object of arrays
   */
  async getStreamEvents(streamKeys, dateRange, includeInitialEvent, grouped = false) {
    if (!streamKeys?.length) return grouped ? {} : [];
    const model = this.#streamMgr.defaultModel;
    return model.asyncPropertyOperation({
      operation: "GET_STREAM_EVENTS",
      streamKeys,
      dateRange,
      includeInitialEvent,
      grouped
    });
  }

  /**
   * Determine if the full range of event data for the given streams is cached and immediately available.
   * @param {string[]} streamKeys array of streamKeys (use intoStreamKey)
   * @param {Array} dateRange [start, end] in milliseconds
   * @returns {boolean} yea or nay
   */
  async isAllDataAvailable(streamKeys, dateRange) {
    if (!streamKeys?.length) return true;
    const model = this.#streamMgr.defaultModel;
    return model.asyncPropertyOperation({
      operation: "GET_STREAM_EVENTS_DATA_COMPLETE",
      streamKeys,
      dateRange
    });
  }

  /**
   * Clear all cached event data. Waits for active requests to finish.
   */
  async clearStreamEvents() {// TODO: add streamKeys, dateRange filters to the clear
    //if (!streamKeys?.length) return true;
    const model = this.#streamMgr.defaultModel;
    return model.asyncPropertyOperation({
      operation: "DT_CLEAR_STREAM_EVENTS_CACHE"
      //streamKeys,
      //dateRange,
    });
  }
}

export function toStreamThresholds(streams, filterStreamKeys) {
  let filterSet = new Set(filterStreamKeys);
  let streamThresholds = [];
  for (const stream of streams) {
    const timeoutMs = (stream.settings.offlineTimeout ?? StreamOfflineTimeouts.Default) * 1000;

    for (const attr of stream.streamAttrs) {
      const streamKey = intoStreamKey(stream.fullId, attr.id);
      if (!filterSet.has(streamKey)) continue;

      const la = attr.allowedValues.thresholds?.lower?.alert;
      const lw = attr.allowedValues.thresholds?.lower?.warn;
      const ua = attr.allowedValues.thresholds?.upper?.alert;
      const uw = attr.allowedValues.thresholds?.upper?.warn;
      streamThresholds.push({ streamKey, thresholds: { la, lw, ua, uw, timeoutMs } });
    }
  }
  return streamThresholds;
}